import * as THREE from 'three';
import { EG } from './enemy-parts';
import { collectMeshes } from '../render/enemy-instances';
/* ============================================================
   BOSS 進戰場用的 rig
   ============================================================

   造型與動畫是 1:1 移植的（`entities/bosses/*.ts`，由 `tools/port-bosses.mjs`
   產生，**不要手改**）。這個檔只做「把那份造型搬進戰場」需要的四件事，
   一件都不動造型本身：

   1. **材質換成這一隻自己的一份。** kit 的 `M.armor` 等是**模組層共用**的，
      戰場的受擊閃白會直接改 `emissive` —— 不換的話打 Boss 會讓圖鑑展示台上
      的那一隻、以及所有共用同一份材質的東西一起閃白，而且沒有任何錯誤。
   2. **收出可以閃白的材質清單。** `M.void` 是 `MeshBasicMaterial`（沒有
      `emissive`），混進去的話閃白那一行會直接拋例外。
   3. **弱點標記。** 規格 §5.2 的可破壞部位要「打亮的地方」讀得出來，
      而 `upd()` 每幀都會覆寫發光強度 —— 疊在造型上改不動，只能另外掛一圈環。
   4. **重建。** Boss 死亡的四拍編排會把 rig 拆成自由剛體（`Object3D.attach()`），
      那顆 root 之後屬於死亡演出。池子裡的那隻要拿回一具完好的身體。 */

/** 弱點標記的材質。**加法混色、不寫深度** —— 它要在裝甲前面看得到。 */
var MARK_MAT = new THREE.MeshBasicMaterial({
  color: 0xffffff, transparent: true, opacity: 0.85,
  depthWrite: false, blending: THREE.AdditiveBlending
});

/** 換一份自己的材質。回傳「可以閃白的那些」。

    ⚠️ `Material.clone()` 在 r128 是用 JSON 深拷 `userData` 的，
    `baseEm`（THREE.Color）會變成純物件。`Color.copy()` 只讀 r/g/b 所以
    不會壞，但這裡照樣重建一份 —— 型別對不上的東西留在資料裡遲早會咬人。 */
function ownMaterials(root) {
  var map: any[] = [], clones: any[] = [], flash: any[] = [];
  root.traverse(function (o: any) {
    if (!o.isMesh || !o.material) return;
    var i = map.indexOf(o.material);
    if (i < 0) {
      var src = o.material, c = src.clone();
      c.userData = {};
      if (src.emissive) {
        c.userData.baseEm = new THREE.Color(src.emissive.getHex());
        c.userData.baseEi = src.emissiveIntensity;
      }
      /* 發光材質的 `userData.base` 是 `upd()` 每幀讀的基礎強度，
         漏掉的話那一隻的發光會變成 NaN（乘一個 undefined）。 */
      if (src.userData && src.userData.base !== undefined) c.userData.base = src.userData.base;
      map.push(src); clones.push(c);
      /* 只有真的有 `emissive` 的才進閃白清單 —— `M.void` 是
         `MeshBasicMaterial`，混進去的話 `m.emissive.copy(...)` 直接拋例外。 */
      if (c.emissive && c.userData.baseEm) flash.push(c);
      i = map.length - 1;
    }
    o.material = clones[i];
  });
  return flash;
}

/** 弱點的環。掛在 root 底下（root 自己已經縮放過），所以 `at` 用 rig 局部座標。
    躺在地面平面上 —— 俯視鏡頭讀得最清楚的方向。 */
function markPart(root, p) {
  var m = new THREE.Mesh(EG.tor, MARK_MAT.clone());
  m.scale.set(p.r * 2.4, p.r * 2.4, p.r * 0.9);
  m.position.set(p.at[0], p.at[1], p.at[2]);
  m.rotation.x = Math.PI / 2;
  m.renderOrder = 3;
  m.userData.noMerge = 1;
  root.add(m);
  return m;
}

/** 建一具戰場用的 BOSS。回傳 `makeEnemy()` 要的那幾個欄位。 */
export function buildBossRig(T) {
  var root = T.build();
  var rig = root.userData.rig;
  var mats = ownMaterials(root);
  root.traverse(function (o: any) { if (o.isMesh) o.castShadow = true; });
  /* 弱點標記要在 `collectMeshes` **之後**才有意義？不 —— 反過來：
     標記也要進 `parts` 清單沒有壞處，但它是自己一份材質，
     閃白不會動到它。清單本身只被實例化路徑用，Boss 不走那條。 */
  var marks: any[] = [];
  if (T.parts) for (var i = 0; i < T.parts.length; i++) marks.push(markPart(root, T.parts[i]));
  return { root: root, body: rig && rig.body ? rig.body : root, rig: rig, mats: mats, marks: marks };
}

/** 把一具用過的 rig 換掉（死亡編排接管了原本那一具）。
    在 `killEnemy` 的交接點呼叫，成本是一次 `build()`，一場最多五次。 */
export function rebuildBossRig(e) {
  var b = buildBossRig(e.T);
  e.root = b.root; e.body = b.body; e.rig = b.rig; e.mats = b.mats; e.marks = b.marks;
  e.parts = collectMeshes(b.root);
  e.root.scale.setScalar(0.01);
  return e;
}
